import { produce } from 'immer';
import {
  parseAsArrayOf,
  parseAsString,
  useQueryStates,
} from 'nuqs';
import React, { useTransition } from 'react';

import { DealershipsByProvince } from '@/services/filters/new-filters.service';
import { ParentFilter } from '@/types/filters/filters.types';

import { NestedCheckboxesSimple } from '../../../nested-checkboxes-simple/nested-checkboxes';
import { NewFilter } from '../new-filter/new-filter';
import { useClearSearch, useQueryNonce } from '../utils';

export const NewLocation: React.FC<{
  dealershipsByProvince: DealershipsByProvince;
  count: number;
  defaultLocation?: string;
  defaultDealershipId?: string;
}> = ({
  dealershipsByProvince = [],
  count,
  defaultLocation,
  defaultDealershipId,
}) => {
  const [_, startTransition] = useTransition();

  const { generate } = useQueryNonce();

  const [locations, setLocations] = useQueryStates(
    {
      dealership: parseAsArrayOf(parseAsString).withDefault(
        defaultDealershipId ? [defaultDealershipId] : [],
      ),
      province: parseAsArrayOf(parseAsString).withDefault(
        defaultLocation ? [defaultLocation.toLowerCase()] : [],
      ),
    },
    {
      history: 'push',
      startTransition,
      shallow: false,
    },
  );

  const syncedOptions = dealershipsByProvince.map((province) => {
    const provinceChecked =
      !!locations.province.find(
        (provinceName) =>
          provinceName.toLowerCase() === province.name.toLowerCase(),
      ) || province.children.every((option) => option.isChecked);

    const dealerships = province.children.map((dealership) => ({
      ...dealership,
      isChecked:
        !!locations.dealership.find(
          (dealershipId) =>
            dealershipId.toLowerCase() === dealership.id.toLowerCase(),
        ) || provinceChecked,
    }));

    return {
      ...province,
      isChecked: provinceChecked,
      children: dealerships,
    };
  });

  const toggleFilter = (options: ParentFilter[], name: string) => {
    return produce(options, (draftOptions) => {
      draftOptions?.forEach((option) => {
        if (option.name === name) {
          option.isChecked = !option.isChecked;
          // set all children:
          option.children?.forEach((child) => {
            child.isChecked = option.isChecked;
          });
        } else {
          option.children?.forEach((child) => {
            if (child.name === name) {
              child.isChecked = !child.isChecked;
            }
          });
        }
      });
    });
  };

  const clearSearch = useClearSearch();

  const setOptionsState = async (options: ParentFilter[]) => {
    const dealerships: string[] = [];
    const provinces: string[] = [];

    clearSearch();

    options.forEach((province) => {
      const checkedDealerships =
        province.children?.filter((dealership) => dealership.isChecked) || [];

      if (
        (province.isChecked && checkedDealerships.length === 0) ||
        checkedDealerships.length === province.children?.length
      ) {
        provinces.push(province.name.toLowerCase());
      } else {
        dealerships.push(
          ...checkedDealerships.map((dealership) => dealership.id),
        );
      }
    });

    await setLocations({
      dealership: dealerships,
      province: provinces,
    });
  };

  return (
    <NewFilter
      label="Locations"
      count={count}
      onClear={() => {
        setLocations(null);
        generate();
      }}
    >
      <NestedCheckboxesSimple
        selectFilter={(option) => {
          setOptionsState(toggleFilter(syncedOptions, option.name));
        }}
        options={syncedOptions}
        setOptionsState={async (newStateUpdater) => {
          const res = newStateUpdater(syncedOptions);

          await setOptionsState(res);
        }}
        resetText="Reset Filters"
        selectAllText="All"
        hideText="Hide Dealerships"
        openText="Show Dealerships"
        allSelected={syncedOptions?.every((item) => item.isChecked) || false}
      />
    </NewFilter>
  );
};
